import styled from "@emotion/styled"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import Skeleton from "@mui/material/Skeleton"
import dayjs from "dayjs"
import {
  Area,
  Line,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  TooltipProps,
  ComposedChart,
} from "recharts"

import { DataItem } from "./BOTGStatsPanel"
import { parseBigIntAsNumber } from "../../utils/format"

interface BOTGPriceChartProps {
  isLoading: boolean
  data: DataItem[]
}

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    const isPending = data.isPending

    return (
      <TooltipContainer>
        <TooltipDate>{dayjs(label).format("MMM D, YYYY")}</TooltipDate>
        <TooltipValue>
          {data.price.toFixed(6)}
          <TooltipLabel>AO per BOTG</TooltipLabel>
        </TooltipValue>
        <TooltipStatus isPending={isPending}>
          {isPending ? (
            <>
              <AccessTimeIcon fontSize="small" />
              <span>Future projection</span>
            </>
          ) : (
            <>
              <CheckCircleIcon fontSize="small" />
              <span>Historical data</span>
            </>
          )}
        </TooltipStatus>
      </TooltipContainer>
    )
  }
  return null
}

export const BOTGPriceChart = ({ isLoading, data }: BOTGPriceChartProps) => {
  if (isLoading) {
    return <Skeleton width="100%" height={400} /> // Increased height to accommodate both charts
  }

  const orderedDataDict: Record<string, { price: number; date: string; isPending: boolean }> = {}

  data.forEach((item) => {
    const date = dayjs(item.Timestamp).format("YYYY-MM-DD")
    let price = 0

    // Handle different DataItem variants
    if ("AoReceived" in item) {
      // Second variant of DataItem with AoReceived and TokenPriceInAo
      price = Number(parseBigIntAsNumber(BigInt(item.TokenPriceInAo ?? 0), 18))
    } else {
      // First variant of DataItem with TotalInflowInTick and TotalMintedInTick
      const inflow = parseBigIntAsNumber(BigInt(item.TotalInflowInTick ?? 0), 12)
      const minted = parseBigIntAsNumber(BigInt(item.TotalMintedInTick ?? 0), 18)
      price = Number(inflow) / Number(minted)
    }

    // Mark as pending if date is in the future
    const isPending = dayjs(date).isAfter(dayjs())

    orderedDataDict[date] = {
      price,
      date: date,
      isPending,
    }
  })

  // For debugging - log all data points
  console.log("All data with pending status:", Object.values(orderedDataDict))

  // Create dummy future data points if no pending points exist
  const orderedData = Object.values(orderedDataDict).sort((a, b) => a.date.localeCompare(b.date))

  // Ensure unique dates by creating a new filtered array if needed
  const uniqueDatesData = Array.from(
    new Map(orderedData.map((item) => [item.date, item])).values(),
  ).sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))

  // Split data into historical and pending arrays for separate styling
  const historicalData = uniqueDatesData.filter((item) => !item.isPending)

  const minPrice = Math.min(...uniqueDatesData.map((item) => item.price))
  const maxPrice = Math.max(...uniqueDatesData.map((item) => item.price))

  // Format Y-axis values to be more readable
  const formatYAxis = (value: number) => {
    return value.toFixed(5)
  }

  // Custom formatter for X-axis ticks
  const formatXAxis = (tickItem: string) => {
    return dayjs(tickItem).format("D MMM")
  }

  // Calculate interval for X-axis ticks based on number of data points
  const calculateTickInterval = () => {
    const dataLength = historicalData.length
    if (dataLength <= 7) return 0 // Show all ticks for 7 or fewer points
    if (dataLength <= 14) return 1 // Show every other tick for 8-14 points
    return Math.floor(dataLength / 7) // Aim for about 7 ticks total
  }

  const xAxisTickInterval = calculateTickInterval()

  // Get the latest price for the header
  const getLatestPrice = () => {
    if (historicalData.length === 0) return null

    // Sort by date descending to get the most recent
    const sortedData = [...historicalData].sort(
      (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf(),
    )

    return sortedData[0]?.price
  }

  // Prepend 0 price before first date if needed
  if (historicalData.length > 0) {
    historicalData.unshift({
      price: 0,
      date: dayjs(uniqueDatesData[0].date).subtract(1, "day").format("YYYY-MM-DD"),
      isPending: false,
    })
  }

  const latestPrice = getLatestPrice()

  return (
    <div>
      <ChartHeader>
        <ChartTitle>BOTG Price</ChartTitle>
        {latestPrice && (
          <LatestPriceContainer>
            <LatestPriceLabel>Latest:</LatestPriceLabel>
            <LatestPriceValue>{latestPrice.toFixed(6)}</LatestPriceValue>
            <LatestPriceUnit>AO/BOTG</LatestPriceUnit>
          </LatestPriceContainer>
        )}
      </ChartHeader>

      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={historicalData}>
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="rgb(var(--theme-color))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="rgb(var(--theme-color))" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <YAxis
              dataKey="price"
              domain={[minPrice * 0.9, maxPrice * 1.05]}
              tickFormatter={formatYAxis}
              width={80}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
            />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              height={30}
              interval={xAxisTickInterval}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
              padding={{ left: 10, right: 10 }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0, 0, 0, 0.05)" }} />
            <CartesianGrid stroke="rgba(0,0,0,0.05)" vertical={false} />

            {/* Area under the line */}
            <Area
              type="monotone"
              dataKey="price"
              fill="url(#colorPrice)"
              fillOpacity={1}
              stroke="none"
              animationDuration={1000}
            />

            {/* Line above the area */}
            <Line
              type="monotone"
              dataKey="price"
              stroke="rgb(var(--theme-color))"
              strokeWidth={2}
              dot={{
                fill: "rgb(var(--theme-color))",
                r: 4,
                strokeWidth: 0,
              }}
              activeDot={{
                fill: "rgb(var(--theme-color))",
                r: 6,
                strokeWidth: 4,
                stroke: "rgba(var(--theme-color), 0.2)",
              }}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  )
}

const ChartContainer = styled.div`
  display: flex;
  width: 100%;
  height: 240px;
  margin-top: 1rem;
  overflow: hidden;
  border-radius: 12px;
  background-color: #fff;
  padding: 16px 8px 8px 0;

  [data-mui-color-scheme="dark"] & {
    background-color: var(--mui-palette-background-paper);
    border: 1px solid var(--mui-palette-divider);
  }
`

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 4px;
`

const ChartTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
`

const LatestPriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  background: rgba(var(--theme-color), 0.08);
  padding: 6px 12px;
  border-radius: 8px;
  border-left: 3px solid rgb(var(--theme-color));
`

const LatestPriceLabel = styled.span`
  font-size: 0.75rem;
  color: var(--mui-palette-text-secondary);
  font-weight: 500;
`

const LatestPriceValue = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgb(var(--theme-color));
  font-feature-settings: "tnum";
  font-variant-numeric: tabular-nums;
`

const LatestPriceUnit = styled.span`
  font-size: 0.7rem;
  color: var(--mui-palette-text-secondary);
`

// Tooltip styles
const TooltipContainer = styled.div`
  background-color: #fff;
  border: 1px solid rgba(var(--theme-color), 0.2);
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 180px;

  [data-mui-color-scheme="dark"] & {
    background-color: var(--mui-palette-background-paper);
  }
`

const TooltipDate = styled.div`
  font-size: 0.875rem;
  color: var(--mui-palette-text-secondary);
  margin-bottom: 8px;
  font-weight: 500;
`

const TooltipValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: rgb(var(--theme-color));
  margin-bottom: 8px;
  display: flex;
  align-items: baseline;
  gap: 6px;
`

const TooltipLabel = styled.span`
  font-size: 0.75rem;
  font-weight: normal;
  color: var(--mui-palette-text-secondary);
`

const TooltipStatus = styled.div<{ isPending: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: ${(props) =>
    props.isPending ? "var(--mui-palette-warning-main)" : "var(--mui-palette-success-main)"};

  svg {
    font-size: 14px;
  }
`

export default BOTGPriceChart
